import axios from 'axios'
import toast from 'react-hot-toast'
import React, { useState, useEffect} from 'react'
import axiosInstance from '../../api/axiosInstance'
import OrderItemsTable from '../tables/OrderItemsTable'
import ConfirmationModal from '../modals/ConfirmationModal'
import Ciudades from '../../../src/assets/data/ciudades.json'
import Departamentos from '../../../src/assets/data/departamentos.json'
import {metodoPago, transportadora, ORDER_STATES, tiposDeDocumento} from '../../assets/data/arrays'
import { Input, Textarea } from '../ui'
import { FormField } from '../ui/Form'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { mapApiProductToOrderDetails } from '../../utils/productStockDisplayUtils'
import { FLEXY_PRODUCTS_MULTIPLE_PATH } from '../../constants/productsApiPaths'

interface OrderDetail {
  id: number
  orders_id: number
  origin_order_id: number
  store_name: string
  status: string
  total: string
  envio?: string
  guia?: string
  date_created: string
  date_modified: string
  agente?: number
  payment_method?: string
  extraDetails?: {
    forma_pago?: string
    payment_method?: string
    customer_note?: string
    transportadora?: string
    transaction_id?: string
    recoge_en_bodega?: number
  }
  customer: {
    first_name: string
    last_name: string
    email: string
    document?: string
    tipo_documento?: string
    tipo_persona?: string
  }
  billingAddress: {
    address_1: string
    address_2?: string
    city: string
    state: string
    country: string
    postcode: string
    phone?: string
  }
  shippingAddress: {
    address_1: string
    address_2?: string
    city: string
    state: string
    country: string
    postcode: string
    phone?: string
  }
  lineItems: Array<{
    product_id: string
    product_name: string
    quantity: number
    price: string
    is_combo?: number
    components?: Array<{
      id_combo?: number
      sku: string
      quantity: number
      name: string
      price: string
      tax_rate: string
      ean12?: string
      peso?: string | null
      longitud?: string | null
      ancho?: string | null
      alto?: string | null
      instrucciones_empaque?: string
      imagen?: string
    }>
  }>
}

interface EntregaPDVFormProps {
  orderDetail?: OrderDetail | null
}

const EntregaPDVForm: React.FC<EntregaPDVFormProps> = ({ orderDetail }) => {
  const [productDetails, setProductDetails] = useState<{[key: string]: any}>({})
  const [loadingDetails, setLoadingDetails] = useState<{[key: string]: boolean}>({})
  const [showEntregaModal, setShowEntregaModal] = useState(false)
  const [entregando, setEntregando] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [expandedCombos, setExpandedCombos] = useState<{[key: string]: boolean}>(() => {
    const initialExpanded: {[key: string]: boolean} = {}
    if (orderDetail?.lineItems) {
      orderDetail.lineItems.forEach(item => { 
        initialExpanded[item.product_id] = true
      })
    }
    return initialExpanded
  })

  useEffect(() => {
    if (orderDetail) {
      loadProductDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail])

  const getCiudadNombre = (codigo: string) => {
    const ciudad = Ciudades.find(c => c.codigo === codigo)
    return ciudad ? ciudad.ciudad : codigo
  }

  const getDepartamentoNombre = (codigo: string) => {
    const depto = Departamentos.find(d => d.Codigo.toString() === codigo)
    return depto ? depto.Nombre : codigo
  }

  const getOrderStateName = (status: string) => { 
    const state = ORDER_STATES.find(s => s.value === status)
    return state ? state.label : status
  }

  const getPaymentMethodName = (method: string) => {
    const paymentMethod = metodoPago.find(p => p.value === method)
    return paymentMethod ? paymentMethod.label : method
  }

  const getTransportadoraName = (transportadoraValue: string) => {
    const transportadoraItem = transportadora.find(t => t.value === transportadoraValue)
    return transportadoraItem ? transportadoraItem.label : transportadoraValue
  }

  const getTipoDocumentoName = (tipoDocumentoValue: string) => {
    const tipoDocumentoItem = tiposDeDocumento.find(t => t.value === tipoDocumentoValue)
    return tipoDocumentoItem ? tipoDocumentoItem.label : tipoDocumentoValue
  }

  // Función para cargar los detalles de los productos
  const loadProductDetails = async () => {
    if (orderDetail?.lineItems?.length) {
      try {
        console.log('🚀 Iniciando carga de detalles de productos...')
        const newDetails: {[key: string]: any} = {}
        const newLoadingDetails: {[key: string]: boolean} = {}
        const allSkus: string[] = []
        orderDetail.lineItems.forEach(item => {allSkus.push(item.product_id);})
        
        // Agregar SKUs de componentes de combos
        orderDetail.lineItems.forEach(item => {
          if (item.is_combo === 1 && item.components) {
              item.components.forEach((component: any) => {allSkus.push(component.sku);})
          }
        })
        
        const uniqueSkus: string[] = Array.from(new Set(allSkus))
    
        uniqueSkus.forEach(sku => { 
          newLoadingDetails[sku] = true
          setLoadingDetails(prev => ({...prev, [sku]: true}))
        })
        
        try { 
          console.log('Cargando detalles de todos los productos:', uniqueSkus)
          const token = localStorage.getItem('accessToken')
          const response = await axiosInstance.get(`${FLEXY_PRODUCTS_MULTIPLE_PATH}?skus=${uniqueSkus.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          
          if (response.data && response.data.products) {
            response.data.products.forEach((product: any) => {
              const sku = product.sku?.toString().replace(/^0+/, '') || ''
              newDetails[sku] = mapApiProductToOrderDetails({ ...product, sku })
            })
          }
          
          // Procesar combos para calcular stock mínimo
          orderDetail.lineItems.forEach(item => {
            if (item.is_combo === 1 && item.components) {
              const stocks: number[] = []
              item.components.forEach((component: any) => {
                const componentDetails = newDetails[component.sku]
                if (componentDetails) {
                  const stockValue = componentDetails.stock || componentDetails.stock_quantity || 0
                  stocks.push(stockValue)
                } else {
                  stocks.push(0)
                }
              })
              
              // Actualizar stock del combo con el mínimo
              if (stocks.length > 0) {
                const minStock = Math.min(...stocks)
                if (newDetails[item.product_id]) {
                    newDetails[item.product_id] = {
                    ...newDetails[item.product_id],
                    stock: minStock,
                    stock_quantity: minStock
                  }
                }
              }
            }
          })
          
        } catch (error) {
          console.error('Error al cargar productos múltiples:', error)
        } finally {
          // Marcar todos como no cargando
          uniqueSkus.forEach(sku => {
            newLoadingDetails[sku] = false
            setLoadingDetails(prev => ({...prev, [sku]: false}))
          })
        }

        console.log('✅ Todos los detalles de productos cargados:', newDetails)
        setProductDetails(newDetails)
      } catch (error) {
        console.error('Error general al cargar detalles:', error)
      }
    }
  }

  const toggleCombo = async (productId: string) => {
    setExpandedCombos(prev => ({...prev,[productId]: !prev[productId]}))

    // Si se está expandiendo el combo, cargar los detalles de los componentes
    if (!expandedCombos[productId]) {
      const productDetail = productDetails[productId]
      if (productDetail?.is_combo === 1 && productDetail?.components) {
        let stocks = []
        
        // Cargar detalles de cada componente
        for (const component of productDetail.components) {
          const compSku = component.sku
          if (!productDetails[compSku] && compSku) {
            setLoadingDetails(prev => ({ ...prev, [compSku]: true }))
            try {
              console.log('Cargando detalles del componente por SKU:', compSku)
              const token = localStorage.getItem('accessToken')
              const response = await axiosInstance.get(`/products/${compSku}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              if (response.data) {
                const mappedComp = mapApiProductToOrderDetails(response.data)
                stocks.push(mappedComp.stock)
                setProductDetails(prev => ({
                  ...prev,
                  [compSku]: mappedComp
                }))
              }
            } catch (error) {
              console.error('Error al cargar el componente:', compSku, error)
              stocks.push(0)
            } finally {
              setLoadingDetails(prev => ({ ...prev, [compSku]: false }))
            }
          } else if (productDetails[compSku]) {
            const stockValue = productDetails[compSku].stock || productDetails[compSku].stock_quantity || 0
            stocks.push(stockValue)
          }
        }

        // Una vez que tenemos todos los stocks, actualizamos el producto principal
        if (stocks.length > 0) {
          const minStock = Math.min(...stocks)
          console.log('Stocks de componentes:', stocks)
          console.log('Stock mínimo para el combo:', minStock)
          
          setProductDetails(prev => ({
            ...prev,
            [productId]: {
              ...prev[productId],
              stock: minStock,
              stock_quantity: minStock
            }
          }))
        }
      }
    }
  }

  const calculateTotals = (items: any[] = []) => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price) * item.quantity)
    }, 0)

    const discount = items.reduce((sum, item) => {
      const d = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0
      return sum + (isNaN(d) ? 0 : d)
    }, 0)

    const taxes = items.reduce((sum, item) => {
      const itemSubtotal = parseFloat(item.price) * item.quantity
      const itemDiscount = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0
      
      // Buscar el IVA en productDetails usando el SKU del item
      const productDetail = productDetails[item.sku || item.product_id] || {}
      const itemIva = productDetail.iva_ofima !== undefined && productDetail.iva_ofima !== null 
        ? Number(productDetail.iva_ofima) / 100 
        : (item.iva !== undefined && item.iva !== null ? Number(item.iva) / 100 : 0.19)
        
      return sum + ((itemSubtotal - itemDiscount) * itemIva)
    }, 0)

    const shipping = parseFloat(orderDetail?.envio || '0')
    const total = subtotal - discount + taxes + shipping

    return {
      subtotal: subtotal.toFixed(2),
      discount: discount.toFixed(2),
      taxes: taxes.toFixed(2),
      shipping: shipping.toFixed(2),
      total: total.toFixed(2)
    }
  }

  // Función para confirmar entrega PDV
  const handleConfirmarEntrega = async () => {
    try {
      setEntregando(true)
      const token = localStorage.getItem("accessToken")
      if (!token) {
        toast.error("Error de autenticación", { icon: "⚠️", position: 'top-center' })
        return
      }

      // Obtener el nombre del usuario del localStorage
      const storedUser = localStorage.getItem('user')
      const userName = storedUser ? JSON.parse(storedUser).first_name + ' ' + JSON.parse(storedUser).last_name : 'Usuario'

      // Preparar los datos para actualizar
      const updateData: any = {
        status: "completed", // Cambiar estado a completed
        old_data_order: {
          status: orderDetail?.status || "asignado",
          customer_note: orderDetail?.extraDetails?.customer_note?.trim() || "",
          line_items: orderDetail?.lineItems || [],
          transaction_id: orderDetail?.extraDetails?.transaction_id?.trim() || ""
        },
        entrega_pdv: {
          fecha: new Date().toISOString().slice(0, 19).replace('T', ' '),
          observaciones: "",
          responsable: userName,
          entregado: true
        }
      }

      console.log('📦 JSON completo que se enviará a la BD:', JSON.stringify(updateData, null, 2))

      const response = await axiosInstance.patch(`/list/${orderDetail?.id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      console.log('✅ Pedido completed exitosamente:', JSON.stringify(response.data, null, 2))
      toast.success("Entrega PDV completed exitosamente", { icon: "✅", position: 'top-center' })
      setShowEntregaModal(false)
      setShowSuccessModal(true)
    } catch (error) {
      console.error("❌ Error al confirmar entrega:", error)
      if (axios.isAxiosError(error)) {
        console.error('📝 Detalles del error:', JSON.stringify({
          status: error.response?.status,
          data: error.response?.data,
          headers: error.response?.headers
        }, null, 2))
      }
      toast.error("Error al confirmar entrega", { icon: "⚠️", position: 'top-center' })
    } finally {
      setEntregando(false)
    }
  }

  const direcionBilling1 = orderDetail?.billingAddress?.address_1 || ''
  const direcionBilling2 = orderDetail?.billingAddress?.address_2 || ''
  const separador = direcionBilling2 ? ', ' : ''

  const direcionShipping1 = orderDetail?.shippingAddress?.address_1 || ''
  const direcionShipping2 = orderDetail?.shippingAddress?.address_2 || ''
  const separadorShipping = direcionShipping2 ? ', ' : ''

  return (
    <>
      <div className="space-y-8">
        {/* Información del Cliente */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Información del Cliente</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="Nombre">
              <Input
                type="text"
                value={`${orderDetail?.customer?.first_name || ''} ${orderDetail?.customer?.last_name || ''}`}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Email">
              <Input
                type="email"
                value={orderDetail?.customer?.email || ''}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Teléfono">
              <Input
                type="text"
                value={orderDetail?.shippingAddress?.phone || orderDetail?.billingAddress?.phone || ''}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Tipo de Documento">
              <Input
                type="text"
                value={getTipoDocumentoName(orderDetail?.customer?.tipo_documento || '')}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Documento">
              <Input
                type="text"
                value={orderDetail?.customer?.document || ''}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Método de Pago">
              <Input
                type="text"
                value={getPaymentMethodName(orderDetail?.extraDetails?.payment_method || '')}
                readOnly
                mode="view"
              />
            </FormField>
          </div>
        </div>

        {/* Direcciones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dirección de Facturación */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Dirección de Facturación</h3>
            <div className="space-y-3">
              <FormField label="Dirección">
                <Input
                  type="text"
                  value={direcionBilling1 + separador + direcionBilling2 || ''}
                  readOnly
                  mode="view"
                />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Departamento">
                  <Input
                    type="text"
                    value={getDepartamentoNombre(orderDetail?.billingAddress?.state || '')}
                    readOnly
                    mode="view"
                  />
                </FormField>
                <FormField label="Ciudad">
                  <Input
                    type="text"
                    value={getCiudadNombre(orderDetail?.billingAddress?.city || '')}
                    readOnly
                    mode="view"
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Dirección de Envío */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-4">Dirección de Envío</h3>
            <div className="space-y-3">
              <FormField label="Dirección">
                <Input
                  type="text"
                  value={direcionShipping1 + separadorShipping + direcionShipping2 || ''}
                  readOnly
                  mode="view"
                />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Departamento">
                  <Input
                    type="text"
                    value={getDepartamentoNombre(orderDetail?.shippingAddress?.state || '')}
                    readOnly
                    mode="view"
                  />
                </FormField>
                <FormField label="Ciudad">
                  <Input
                    type="text"
                    value={getCiudadNombre(orderDetail?.shippingAddress?.city || '')}
                    readOnly
                    mode="view"
                  />
                </FormField>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de ENVÍO */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-4">Envío</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <FormField label="Transportadora">
              <Input
                type="text"
                value={orderDetail?.extraDetails?.transportadora ? getTransportadoraName(orderDetail.extraDetails.transportadora) : 'No asignada'}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Guía">
              <Input
                type="text"
                value={orderDetail?.guia || 'No asignada'}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Costo de envío">
              <Input
                type="text"
                value={`$${parseFloat(orderDetail?.envio || '0').toLocaleString('es-CO')}`}
                readOnly
                mode="view"
              />
            </FormField>
            <FormField label="Estado">
              <Input
                type="text"
                value={getOrderStateName(orderDetail?.status || '')}
                readOnly
                mode="view"
              />
            </FormField>
          </div>
          {orderDetail?.extraDetails?.customer_note && (
            <FormField label="Notas" className="mt-4">
              <Textarea
                value={orderDetail?.extraDetails?.customer_note || ''}
                readOnly
                disabled={true}
                rows={2}
                mode="view"
              />
            </FormField>
          )}
        </div>

        {/* Productos del Pedido */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800">Productos del Pedido</h3>
          </div>

          <OrderItemsTable
            lineItems={orderDetail?.lineItems || []}
            productDetails={productDetails}
            loadingDetails={loadingDetails}
            expandedCombos={expandedCombos}
            mode="view"
            formDataStatus={orderDetail?.status}
            onQuantityChange={(index: number, quantity: number) => {console.log('Cambio de cantidad no permitido en modo view');}}
            onRemoveProduct={(index: number) => {console.log('Eliminación no permitida en modo view');}}
            onDiscountChange={(index: number, discount: number) => {console.log('Cambio de descuento no permitido en modo view');}}
            onPriceChange={(index: number, price: number) => {console.log('Cambio de precio no permitido en modo view');}}
            onToggleCombo={toggleCombo}
            calculateTotals={calculateTotals}
            shippingCost={'0'}
          />
        </div>

        {/* Botón Confirmar Entrega PDV */}
        <div className="flex justify-end">
          <button 
            type="button" 
            onClick={() => setShowEntregaModal(true)}
            className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors">
            Entregar Pedido
          </button>
        </div>
      </div>

      {/* Modal de Confirmación de Entrega */}
      {showEntregaModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 >Confirmar Entrega PDV</h2>
              <button 
                onClick={() => { 
                  setShowEntregaModal(false)
                }} 
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Mensaje de validación */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-yellow-800 mb-3">
                      ¿Seguro que quieres cambiar el estado del pedido?
                    </h3>
                    <p className="text-sm text-yellow-700">
                      Valida por favor el número del pedido y la identidad del cliente para realizar una entrega correcta.
                    </p>
                  </div>
                </div>
              </div>

              {/* Información del pedido para validación */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Información a Validar:</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Número de Pedido:</span>
                    <span className="font-bold text-gray-900">#{orderDetail?.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cliente:</span>
                    <span className="font-bold text-gray-900">
                      {orderDetail?.customer?.first_name} {orderDetail?.customer?.last_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Documento:</span>
                    <span className="font-bold text-gray-900">{orderDetail?.customer?.document || 'No especificado'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-bold text-gray-900">${parseFloat(orderDetail?.total || '0').toLocaleString('es-CO')}</span>
                  </div>
                </div>
              </div>


              <div className="flex justify-end space-x-4 mt-6">
                <button 
                  onClick={() => { 
                    setShowEntregaModal(false)
                  }} 
                  disabled={entregando}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleConfirmarEntrega} 
                  disabled={entregando}
                  className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors ${
                    entregando ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {entregando ? 'Procesando...' : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Éxito */}
      <ConfirmationModal
        isOpen={showSuccessModal} 
        onClose={() => { setShowSuccessModal(false); window.location.reload(); }}
        title="Entrega Completed" 
        message="La entrega PDV ha sido completed exitosamente."
        type="success" 
        onConfirm={() => { setShowSuccessModal(false); window.location.reload(); }}
        confirmText="Aceptar"
      />
    </>
  )
}

export default EntregaPDVForm
